import { useRef } from 'react';
import { Trans } from '@lingui/react';
import { useTrackEvent, useAppConfiguration } from '@wkda/funnel-components';
import { useTrackElementView } from '../../../hooks/useTrackElementView';
import { getAutoheroUrl, getWkdaLogoUrl } from '../../../constants/carlead/crossPromoBannerConfig';
import TrustpilotAutohero from './TrustpilotAutohero';

/**
 * CarleadCrossPromoBannerAH - Autohero cross-promotion banner for car lead funnels
 *
 * Displays a partnership banner promoting Autohero's car buying service with:
 * - Logo partnership (WKDA × Autohero)
 * - Headline and subtitle
 * - CTA button redirecting to Autohero search
 * - Trustpilot reviews widget
 * - View and click tracking
 *
 * @returns {JSX.Element} Cross-promo banner component
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const CarleadCrossPromoBannerAH = () => {
  const bannerRef = useRef(null);
  const track = useTrackEvent();
  const {
    fullLocale,
    trackLandingPageType
  } = useAppConfiguration();

  // Get locale-specific URLs
  const wkdaLogoUrl = getWkdaLogoUrl(fullLocale);
  const autoheroUrl = getAutoheroUrl(fullLocale);

  // Track banner visibility
  useTrackElementView(bannerRef, {
    category: 'Cross promotion - AH Smart Car Finder',
    action: 'Visible',
    label: 'Element Visible'
  }, trackLandingPageType);
  const handleCtaClick = () => {
    track({
      action: 'eventTracking',
      category: trackLandingPageType,
      eventData: {
        category: 'Cross promotion - AH Smart Car Finder',
        action: 'Click',
        label: 'CTA'
      }
    });
    window.open(autoheroUrl, '_blank');
  };
  return /*#__PURE__*/_jsx("section", {
    ref: bannerRef,
    className: "carlead-cross-promo-banner",
    "data-testid": "carlead-cross-promo-banner",
    children: /*#__PURE__*/_jsx("div", {
      className: "container",
      children: /*#__PURE__*/_jsxs("div", {
        className: "carlead-cross-promo-banner__content",
        children: [/*#__PURE__*/_jsxs("div", {
          className: "carlead-cross-promo-banner__logos",
          children: [/*#__PURE__*/_jsx("img", {
            src: wkdaLogoUrl,
            alt: "wirkaufendeinauto",
            className: "carlead-cross-promo-banner__logo-wkda"
          }), /*#__PURE__*/_jsx("span", {
            className: "carlead-cross-promo-banner__logo-separator",
            children: "\xD7"
          }), /*#__PURE__*/_jsx("img", {
            src: "https://content.wirkaufendeinauto.de/static/car_images/ah-logo-blue-orange-cross-promo.svg",
            alt: "Autohero",
            className: "carlead-cross-promo-banner__logo-autohero"
          })]
        }), /*#__PURE__*/_jsx("h2", {
          className: "carlead-cross-promo-banner__headline",
          children: /*#__PURE__*/_jsx(Trans, {
            id: "carlead.crossPromoBanner.headline"
          })
        }), /*#__PURE__*/_jsx("p", {
          className: "carlead-cross-promo-banner__subtitle",
          children: /*#__PURE__*/_jsx(Trans, {
            id: "carlead.crossPromoBanner.subtitle",
            components: [/*#__PURE__*/_jsx("strong", {}), /*#__PURE__*/_jsx("strong", {}), /*#__PURE__*/_jsx("br", {
              className: "d-none d-md-inline"
            }), /*#__PURE__*/_jsx("br", {
              className: "d-inline d-md-none"
            })]
          })
        }), /*#__PURE__*/_jsxs("button", {
          onClick: handleCtaClick,
          className: "btn btn-secondary btn-lg btn-arrow carlead-cross-promo-banner__cta",
          "data-tracking": "true",
          "data-action": "Click",
          "data-label": "Cross promotion - AH Smart Car Finder",
          children: [/*#__PURE__*/_jsx(Trans, {
            id: "carlead.crossPromoBanner.cta"
          }), /*#__PURE__*/_jsx("svg", {
            className: "svg-icon ml-2",
            xmlns: "http://www.w3.org/2000/svg",
            width: "1em",
            height: "1em",
            viewBox: "0 0 32 32",
            children: /*#__PURE__*/_jsx("path", {
              d: "m23.999 18 2-2-16-16L6 3.999 17.998 16 6 28.001 9.999 32l14-14z"
            })
          })]
        }), /*#__PURE__*/_jsx(TrustpilotAutohero, {})]
      })
    })
  });
};